import { ReactNode } from 'react';
import { Product, ProductType } from '@v2/shared/lib/types';
import { cn } from '@v2/shared/lib/utils';
import { reportImpression, useImpression } from '@v2/shared/lib/analytics';
import ProductLink from '@v2/shared/components/ProductLink';
import Msrp from '@v2/shared/components/Msrp';
import Savings from '@v2/shared/components/Savings';
import SalePrice from '@v2/shared/components/SalePrice';
import ReviewStars from '@v2/shared/components/ReviewStars';
import ProductImage from '@v2/shared/components/ProductImage';
import OnSaleBadge from './OnSaleBadge';
import Variant from './Variant';
import { COVER_ASPECT, CoverAspect } from './coverAspect';

interface Props {
  product: Product;
  className?: string;
  /**
   * The commercial half of the card: review stars, term, price, msrp and savings.
   *
   * Off leaves the cover and the name, which is what a browse strip wants — the bulk
   * order page sells through a quote form, so a per-issue price beside a title nobody
   * can add to a cart there is noise at best and misleading at worst.
   */
  showDetails?: boolean;
  /** The "Save X%" line under the price. On unless a page says otherwise. */
  showSavings?: boolean;
  /**
   * The struck-through cover (or list) price beside the price. On unless a page says
   * otherwise — the category page turns it off where the storefront hides cover prices.
   */
  showMsrp?: boolean;
  /**
   * The shape of the cover box. Decided by the band, not here — every card in a row
   * needs the same one or the text below them goes ragged again, which is the whole
   * reason the box exists.
   */
  coverAspect?: CoverAspect;
  /**
   * Open the product in a new tab, keeping the page behind it.
   *
   * Off by default, because on a storefront listing the product page is the next step
   * rather than a detour. A page that is not a step towards checkout — the bulk order
   * page, where the conversion is the quote form — wants the opposite: a visitor who
   * opens a title to look at it should not lose the form they were filling in.
   */
  openInNewTab?: boolean;
  /**
   * The GA4 `item_list_name` this card is part of — its band's heading.
   *
   * Set it and the card reports a `view_item_list` impression for itself once it has been
   * on screen; leave it out and it reports nothing. Named rather than derived because a
   * list is a thing reports are grouped by, and a card cannot see the heading above it.
   */
  list?: string;
  /** Position in that list, from 1. Only read when `list` is set. */
  index?: number;
}

export default function ProductCard({
  product,
  className,
  showDetails = true,
  showSavings = true,
  showMsrp = true,
  coverAspect = 'standard',
  openInNewTab = false,
  list,
  index,
}: Props) {
  const baseSku = product.base_sku;

  // On the cover rather than on the card, because the card is a `ProductLink` and an
  // anchor takes no ref. The cover is most of the card's height, so the two answer the
  // same question — and a card whose cover is on screen is a card that was seen.
  const impression = useImpression(() => {
    if (list) {
      reportImpression({
        list,
        index,
        productId: product.id,
        baseSkuCode: baseSku?.code,
        name: product.name,
        price: baseSku?.sale_price || baseSku?.price,
      });
    }
  });

  const hasReviews =
    product.product_type === ProductType.TYPE_MAGAZINE &&
    product.reviews_data &&
    product.reviews_data.count > 0;

  return (
    <ProductLink
      product={product}
      className={className}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener' : undefined}
    >
      {/*
        * A fixed box so the text below every card starts at the same line.
        *
        * Covers are not one shape — across a homepage they run from 0.645 to 0.893
        * wide-for-tall — so at a fixed width their heights differ by around 20% and
        * everything under them lands ragged. Stretching them to match would trade a
        * visible problem for a worse one, so the box is fixed and the cover fills it,
        * losing a little off the bottom instead.
        *
        * object-position: top keeps the masthead and takes the crop off the foot of
        * the cover, which is the least valuable strip. With the box chosen from the
        * row's median that is around 2% for magazines and 1% for books; one shared
        * box for both would have been 12%.
        */}
      <div ref={impression} className={cn('relative flex items-end justify-center mb-4 overflow-hidden', COVER_ASPECT[coverAspect])}>
        {product.on_sale && <OnSaleBadge />}
        <ProductImage
          product={product}
          className="w-full h-full object-cover object-top"
        />
      </div>
      {showDetails && hasReviews && (
        <ReviewStars
          className="flex items-center text-xs mb-2"
          rating={product.reviews_data!.rating}
          reviewsCount={product.reviews_data!.count}
        />
      )}
      <div>
        <Name>{product.name}</Name>
        {showDetails && (
          <>
            <Variant product={product} />
            <div>
              <SalePrice sku={baseSku} className="mr-1" />
              {showMsrp && <Msrp sku={baseSku} className="text-xs" productType={product.product_type} />}
            </div>
            {showSavings && <Savings className="text-sm block" sku={baseSku} />}
          </>
        )}
      </div>
    </ProductLink>
  );
}

function Name({ children }: { children: ReactNode }) {
  return <div className="block font-semibold text-sm truncate">{children}</div>;
}
